/**
 * The one way Nexus Web talks to Nexus.
 *
 * Same origin, always. The session cookie is `HttpOnly`, host-only and
 * `SameSite=Lax`, so there is no token to attach and nothing for script to
 * mishandle — the browser sends the cookie because the request goes back where
 * it came from. That is also why there is no base URL to configure: a
 * configurable API origin would be a configurable way to break the cookie.
 *
 * Everything else here exists so screens do not each reinvent it:
 *
 * - a correlation id on every request, so a screen and the audit record can be
 *   put side by side afterwards;
 * - the error envelope parsed into a `NexusError` that keeps the code and the
 *   correlation id, rather than a status number and a string;
 * - `Idempotency-Key` on mutations that accept one, because a retry the user
 *   did not intend is still a retry;
 * - `401` handled once, by sending the browser to sign in and come back to the
 *   page it was on.
 */

import { NexusError, TransportError, type ErrorBody } from "./errors";

const API = "/api/nexus/v1";
const CORRELATION_HEADER = "x-correlation-id";
const IDEMPOTENCY_HEADER = "Idempotency-Key";

export interface RequestOptions {
  /** Query parameters. `undefined` and `null` values are dropped. */
  params?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Send an idempotency key, for a mutation that should not happen twice. */
  idempotent?: boolean;
  /**
   * Use this correlation id rather than minting one.
   *
   * Only reason to: the caller needs to *show* it. Nexus stamps a correlation
   * id on every audit event, so a screen that made a change and then cannot
   * name the id has left the operator to search the trail by hand. Supplying
   * it here is what lets the screen render a link straight to the evidence.
   *
   * It is traceability and nothing else. It does not make a request safe to
   * repeat — that is `Idempotency-Key`, which is a different contract that not
   * every endpoint implements.
   */
  correlationId?: string;
  /**
   * What a 401 means for this call.
   *
   * `"redirect"`, the default, sends the person to sign in again: a request
   * that comes back 401 mid-session means the session went away, and that is
   * the right answer for it.
   *
   * `"throw"` reports the 401 to the caller and navigates nowhere. Exactly one
   * caller wants this — the probe that asks whether a session exists at all,
   * where 401 is the answer rather than a failure. Redirecting there navigates
   * away from the login page before anyone can see it.
   */
  onUnauthenticated?: "redirect" | "throw";
}

function newCorrelationId(): string {
  return crypto.randomUUID();
}

function url(path: string, params?: RequestOptions["params"]): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return `${API}${path}${query ? `?${query}` : ""}`;
}

/** Send the browser to sign in, and back to where it was. */
export function signIn(returnPath?: string): void {
  const here = returnPath ?? window.location.pathname + window.location.search;
  window.location.assign(`/auth/login?return_path=${encodeURIComponent(here)}`);
}

/**
 * End the session, then land on the front door.
 *
 * `POST /auth/logout` answers `{"signed_out": true}`. A native form submission
 * navigates to that response, so the browser rendered the JSON as a page. The
 * endpoint is right — it is a documented API — so the fix belongs here.
 *
 * Still a POST, and still same-origin, so the `SameSite=Lax` host-only cookie
 * protects it exactly as it protected the form.
 *
 * Navigate in both cases, but only claim a sign-out when one actually
 * happened. If the request rejects (offline, or it never landed) or the
 * server answers non-OK, the session may well still be live — telling the
 * person otherwise is exactly the wrong lie to tell on a shared machine. The
 * button must still visibly do something, so it always navigates; it just
 * navigates to plain `/login` rather than `/login?signed_out=1` when nothing
 * was actually undone. `/login` sits outside the session gate, so a still-valid
 * session resolves there and `Shell`'s `/login` route sends the person back to
 * the application — honestly reflecting that they are still signed in.
 */
export async function signOut(): Promise<void> {
  let signedOut = false;
  try {
    const response = await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
    signedOut = response.ok;
  } catch {
    signedOut = false;
  }
  window.location.assign(signedOut ? "/login?signed_out=1" : "/login");
}

async function send<T>(method: string, path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    [CORRELATION_HEADER]: options.correlationId ?? newCorrelationId(),
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (options.idempotent) {
    headers[IDEMPOTENCY_HEADER] = crypto.randomUUID();
  }

  let response: Response;
  try {
    response = await fetch(url(path, options.params), {
      method,
      headers,
      // Same origin only. `include` would invite a cross-origin deployment,
      // which is the arrangement the cookie is designed to refuse.
      credentials: "same-origin",
      redirect: "error",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw cause;
    }
    throw new TransportError("Nexus could not be reached.", { cause });
  }

  if (response.status === 401) {
    if (options.onUnauthenticated !== "throw") {
      signIn();
    }
    throw new NexusError(401, { code: "UNAUTHENTICATED", message: "Your session has ended." });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();

  // Not every failure arrives in the envelope. A proxy, or an unhandled
  // exception that never reached the error handler, answers with plain text —
  // and parsing that as JSON would throw here, escape as a `SyntaxError`, and
  // be reported to the user as though the network were down. The platform
  // failing and the network failing are different things, and telling somebody
  // the wrong one sends them to the wrong place.
  let parsed: unknown = null;
  let parseable = true;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parseable = false;
  }

  if (!response.ok) {
    const envelope = parseable ? (parsed as { error?: ErrorBody } | null) : null;
    throw new NexusError(
      response.status,
      envelope?.error ?? {
        code: response.status >= 500 ? "PLATFORM_ERROR" : "UNKNOWN",
        message:
          response.status >= 500
            ? "Nexus could not complete this. The failure is on the platform, not with your request."
            : `Nexus answered ${String(response.status)}.`,
        // The correlation id the platform stamps on every response, so a
        // failure that never reached the error handler is still findable.
        correlation_id: response.headers.get("x-correlation-id"),
      },
    );
  }
  if (!parseable) {
    throw new NexusError(response.status, {
      code: "PLATFORM_ERROR",
      message: "Nexus answered with something this application could not read.",
      correlation_id: response.headers.get("x-correlation-id"),
    });
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => send<T>("GET", path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    send<T>("POST", path, body ?? {}, options),
};
